import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PLACEHOLDER_IMAGE_SVG } from '../config/constants';
import { FiLogOut, FiEdit } from 'react-icons/fi';

const Profile: React.FC = () => {
  const { user, loading, signOut, hasRole } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/');
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error('Failed to sign out. Please try again.');
    }
  };

  // Show loading spinner while auth state is resolving
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect or show message if user is not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Access Denied
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please sign in to view your profile.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="mt-5 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // --- Profile Display --- 
  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex justify-between items-center">
              {/* User Info */}
              <div className="flex items-center space-x-4">
                <img
                  src={user.photoURL || PLACEHOLDER_IMAGE_SVG}
                  alt={user.displayName || 'User Avatar'}
                  className="h-16 w-16 rounded-full object-cover border-2 border-gray-200"
                  onError={(e) => { 
                    e.currentTarget.src = PLACEHOLDER_IMAGE_SVG; 
                    e.currentTarget.onerror = null; 
                  }}
                />
                <div>
                  <h3 className="text-xl leading-6 font-semibold text-gray-900">
                    {user.displayName || 'Welcome!'}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {user.email}
                  </p>
                  
                  {/* Display Roles */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {user.roles && user.roles.map((role) => (
                      <span 
                        key={role}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                          ${role === 'admin' ? 'bg-red-100 text-red-800' : 
                            role === 'organizer' ? 'bg-purple-100 text-purple-800' : 
                            'bg-blue-100 text-blue-800'}`}
                      >
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/profile/edit')}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <FiEdit className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                  Edit Profile
                </button>
                <button
                  onClick={handleSignOut}
                  className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FiLogOut className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
          
          {/* User Stats */}
          {user.stats && (
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Events Created</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.stats.eventsCreated}</dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Events Attended</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.stats.eventsAttended}</dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Tickets Purchased</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.stats.ticketsPurchased}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* Conditional Content based on Roles */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {hasRole('organizer') || hasRole('admin') ? 'My Events' : 'My Bookings'}
            </h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            {hasRole('organizer') || hasRole('admin') ? (
              <div>
                <p className="text-sm text-gray-500 mb-4">Events you've organized will appear here.</p>
                <button
                  onClick={() => navigate('/events/create')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Create New Event
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Your bookings will appear here once you've purchased tickets.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;